import { apiFetch } from '@/api/client'
import type { Address, Salutation } from '@/api/customers'
import type { SortDirection } from '@/api/sorting'

/**
 * Four states, carried by the backend and by the iOS app.
 *
 * Nothing in this frontend shows or sets one any more: a lead is the conversation that was had,
 * and where it goes from there is tracked elsewhere. The type stays because the field is on the
 * wire in both directions — an update has to send back the status the lead already has, or the
 * backend's default would overwrite it.
 */
export const LeadStatus = {
  NEW: 'NEW',
  IN_PROGRESS: 'IN_PROGRESS',
  WON: 'WON',
  LOST: 'LOST',
} as const

export type LeadStatus = (typeof LeadStatus)[keyof typeof LeadStatus]

/**
 * A row in the lead list.
 *
 * The backend returns only the leads the caller may see — whoever wrote it, whoever it was handed
 * to, and any administrator — and filters that **in the query**, so `total` is the count of what
 * this user may see, not of what exists.
 */
export type LeadSummary = {
  id: string
  tradeFairId: string
  tradeFairName: string
  /** The frozen company name: what it was called when the lead was written, not today. */
  companyName: string
  contactName: string | null
  status: LeadStatus
  capturedAt: string
  ownerId: string
  assigneeId: string | null
  /** Whether the frozen copy has been taken into the customer master data yet. */
  linkedToCustomer: boolean
}

export type LeadPage = {
  leads: LeadSummary[]
  page: number
  size: number
  total: number
}

/**
 * The columns the lead table has. The backend refuses anything else with a 400.
 *
 * It also offers `STATUS`, which is deliberately not here: no column shows a status, so nothing
 * could produce that order — and a `?sort=STATUS` left in an old bookmark falls back to the
 * default rather than sorting by a value the table does not print.
 */
export const LeadSort = {
  CAPTURED_AT: 'CAPTURED_AT',
  COMPANY_NAME: 'COMPANY_NAME',
  TRADE_FAIR: 'TRADE_FAIR',
} as const

export type LeadSort = (typeof LeadSort)[keyof typeof LeadSort]

/** What `assignee` takes besides a user id: the leads nobody has been given. */
export const ASSIGNEE_NOBODY = 'NOBODY'

export type LeadListQuery = {
  search?: string
  tradeFairId?: string | null
  /** For an administrator, who otherwise sees everybody's. Redundant for everybody else. */
  mine?: boolean
  /**
   * Whether the frozen copy has been taken into the customer master data. Undefined is "either";
   * `false` is the list somebody works through after a fair.
   */
  linked?: boolean | null
  /**
   * A user id, or `ASSIGNEE_NOBODY`. Narrows within what the caller may see, never beyond it: a
   * salesperson asking for a colleague gets the leads they handed to that colleague, not the
   * colleague's whole desk.
   */
  assignee?: string | null
  sort?: LeadSort | null
  direction?: SortDirection | null
  page?: number
  size?: number
}

export const DEFAULT_PAGE_SIZE = 20

export function listLeads(query: LeadListQuery = {}, signal?: AbortSignal): Promise<LeadPage> {
  const parameters = new URLSearchParams()
  if (query.search) {
    parameters.set('search', query.search)
  }
  if (query.tradeFairId) {
    parameters.set('tradeFairId', query.tradeFairId)
  }
  if (query.mine) {
    parameters.set('mine', 'true')
  }
  // Only when it is actually set: absent means "either", and sending `false` by default would
  // hide every lead whose company is already in the master data.
  if (query.linked === true || query.linked === false) {
    parameters.set('linked', String(query.linked))
  }
  if (query.assignee) {
    parameters.set('assignee', query.assignee)
  }
  if (query.sort) {
    parameters.set('sort', query.sort)
  }
  // Only alongside a column: a direction on its own would order the default column backwards,
  // which is not what any click produced.
  if (query.sort && query.direction) {
    parameters.set('direction', query.direction)
  }
  parameters.set('page', String(query.page ?? 0))
  parameters.set('size', String(query.size ?? DEFAULT_PAGE_SIZE))
  return apiFetch<LeadPage>(`/api/leads?${parameters}`, { signal })
}

// --- one lead, whole ----------------------------------------------------------------------------

/**
 * A contact as the lead froze them. `sourceContactId` says where the copy came from and is never
 * read back through — it is provenance, not a reference.
 */
export type LeadContact = {
  id: string
  sourceContactId: string | null
  salutation: Salutation
  firstName: string | null
  lastName: string
  position: string | null
  email: string | null
  phone: string | null
  mobile: string | null
}

export type LeadCustomer = {
  id: string
  /** Null while the company was typed in free at the stand and not yet taken into the master data. */
  sourceCustomerId: string | null
  companyName: string
  customerNumber: string | null
  address: Address
  phone: string | null
  email: string | null
  website: string | null
  contacts: LeadContact[]
}

export type LeadInterest = {
  id: string
  name: string
  categoryId: string
  categoryName: string
}

export type LeadNote = {
  id: string
  authorId: string
  text: string
  createdAt: string
}

export type Lead = {
  id: string
  tradeFairId: string
  tradeFairName: string
  customer: LeadCustomer
  status: LeadStatus
  capturedAt: string
  summary: string | null
  ownerId: string
  assigneeId: string | null
  interests: LeadInterest[]
  notes: LeadNote[]
}

// --- what is sent -------------------------------------------------------------------------------

export type LeadContactInput = {
  /** Present for a contact already on the lead: it keeps its row instead of being replaced. */
  id?: string
  sourceContactId?: string | null
  salutation: Salutation
  firstName: string | null
  lastName: string
  position: string | null
  email: string | null
  phone: string | null
  mobile: string | null
}

export type LeadCustomerInput = {
  /**
   * Provenance only. Sending it does not make the lead read anything from that record, and the
   * backend ignores it on a plain update — promoting or linking is what owns this field.
   */
  sourceCustomerId?: string | null
  companyName: string
  customerNumber: string | null
  address: Address
  phone: string | null
  email: string | null
  website: string | null
}

export type LeadInput = {
  tradeFairId: string
  customer: LeadCustomerInput
  contacts: LeadContactInput[]
  status: LeadStatus
  /** Omitted means now — the conversation is being written up as it happens. */
  capturedAt?: string | null
  summary: string | null
  interestIds: string[]
  assigneeId?: string | null
}

export function getLead(id: string, signal?: AbortSignal): Promise<Lead> {
  return apiFetch<Lead>(`/api/leads/${encodeURIComponent(id)}`, { signal })
}

export function createLead(body: LeadInput): Promise<Lead> {
  return apiFetch<Lead>('/api/leads', { method: 'POST', body: JSON.stringify(body) })
}

/**
 * The lead, its contacts **and** the photographed cards, in one request.
 *
 * A stand's network is what this is for: create-then-upload-then-upload is three chances to
 * half-save a conversation. The cards are addressed by the position of their contact in
 * `body.contacts`, which is the only key there is — the contacts have no ids until this call
 * creates them.
 *
 * `FormData` sets its own content type, boundary included; `apiFetch` knows not to touch it.
 */
export function captureLead(body: LeadInput, cards: Map<number, File[]>): Promise<Lead> {
  const form = new FormData()
  // A Blob rather than a string, so the part carries `application/json` and Spring binds it to
  // the request object instead of treating it as a form field.
  form.append('lead', new Blob([JSON.stringify(body)], { type: 'application/json' }))
  for (const [position, files] of cards) {
    // Two parts of the same name are the two sides of one card.
    files.forEach((file) => form.append(`card-${position}`, file, file.name))
  }
  // Its own path rather than the same one with a different content type: OpenAPI has one
  // operation per path and method, and a second handler on `/api/leads` took the JSON endpoint
  // out of the generated contract entirely.
  return apiFetch<Lead>('/api/leads/capture', { method: 'POST', body: form })
}

export function updateLead(id: string, body: LeadInput): Promise<Lead> {
  return apiFetch<Lead>(`/api/leads/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

/**
 * What a batch change did.
 *
 * `skipped` holds the ids that were left alone: not there, or not this caller's to change. The
 * backend does not say which, for the same reason a single lead answers 404 either way.
 */
export type LeadBulkResult = {
  updated: string[]
  skipped: string[]
}

/** Owner or administrator per lead, as for a single one. Null takes the assignments back. */
export function assignLeads(ids: string[], assigneeId: string | null): Promise<LeadBulkResult> {
  return apiFetch<LeadBulkResult>('/api/leads/bulk/assignee', {
    method: 'POST',
    body: JSON.stringify({ ids, assigneeId }),
  })
}

/** Owner or administrator only — seeing a lead is not the same as being free to pass it on. */
export function assignLead(id: string, assigneeId: string | null): Promise<Lead> {
  return apiFetch<Lead>(`/api/leads/${encodeURIComponent(id)}/assignee`, {
    method: 'PUT',
    body: JSON.stringify({ assigneeId }),
  })
}

export function deleteLead(id: string): Promise<void> {
  return apiFetch<void>(`/api/leads/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

/** Appended, never overwritten: once a lead is handed on, two people are writing on it. */
export function addLeadNote(id: string, text: string): Promise<LeadNote> {
  return apiFetch<LeadNote>(`/api/leads/${encodeURIComponent(id)}/notes`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  })
}

/**
 * Takes a note back off the lead. Whoever wrote it, or an administrator — the backend answers
 * `LEAD_NOTE_NOT_AUTHOR` to anybody else, and 404 for a note that is not on this lead.
 */
export function deleteLeadNote(id: string, noteId: string): Promise<void> {
  return apiFetch<void>(
    `/api/leads/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`,
    { method: 'DELETE' },
  )
}

/**
 * Creates a customer from the frozen copy and points the copy at it. The copied fields stay
 * exactly as they were — that is the whole point of having frozen them.
 */
export function promoteLeadCustomer(id: string): Promise<Lead> {
  return apiFetch<Lead>(`/api/leads/${encodeURIComponent(id)}/customer`, { method: 'POST' })
}

/** For when the company turns out to have been in the master data all along. */
export function linkLeadCustomer(id: string, customerId: string): Promise<Lead> {
  return apiFetch<Lead>(`/api/leads/${encodeURIComponent(id)}/customer`, {
    method: 'PUT',
    body: JSON.stringify({ customerId }),
  })
}
